"use client";

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, Square, Send, X, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

interface VoiceRecorderInputProps {
  onVoiceSend: (audioBlob: Blob) => void;
  onCancel: () => void;
  targetId: string;
}

type PermissionStatus = 'idle' | 'granted' | 'denied';

export default function VoiceRecorderInput({
  onVoiceSend,
  onCancel,
  targetId
}: VoiceRecorderInputProps) {
  const { toast } = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('idle');
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Request microphone permission on mount
  useEffect(() => {
    checkMicrophonePermission();
    return () => {
      // Cleanup on unmount
      stopRecording();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  const checkMicrophonePermission = async () => {
    try {
      // Check if permission API is available
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (result.state === 'granted') {
          setPermissionStatus('granted');
        } else if (result.state === 'denied') {
          setPermissionStatus('denied');
        }
      }
    } catch (error) {
      // Permission API not supported, will request on first click
      console.log('Permission API not available, will request on first use');
    }
  };

  const requestMicrophoneAccess = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setPermissionStatus('granted');
      return true;
    } catch (error: any) {
      console.error('Error requesting microphone access:', error);
      setPermissionStatus('denied');
      toast({
        title: "Microphone Access Denied",
        description: "Please allow microphone access to record voice replies.",
        variant: "destructive",
      });
      return false;
    }
  };

  const startRecording = async () => {
    if (isRecording) return;

    // Request permission if not already granted
    if (permissionStatus !== 'granted') {
      const granted = await requestMicrophoneAccess();
      if (!granted) return;
    }

    // Ensure we have a stream
    if (!streamRef.current) {
      const granted = await requestMicrophoneAccess();
      if (!granted) return;
    }

    try {
      audioChunksRef.current = [];
      setRecordingTime(0);

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(streamRef.current!, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setIsRecording(false);
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        toast({
          title: "Recording Error",
          description: "An error occurred while recording. Please try again.",
          variant: "destructive",
        });
        stopRecording();
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error: any) {
      console.error('Error starting recording:', error);
      toast({
        title: "Recording Failed",
        description: error.message || "Failed to start recording. Please try again.",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  };

  const handleSend = async () => {
    if (!audioBlob) return;

    setIsSending(true);
    try {
      await onVoiceSend(audioBlob);
      // Reset state after successful send
      setAudioBlob(null);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error sending voice message:', error);
      toast({
        title: "Send Failed",
        description: "Failed to send voice message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
    onCancel();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Phase 1: Idle - Show microphone button
  if (!isRecording && !audioBlob) {
    return (
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={startRecording}
          className="text-slate-400 hover:text-blue-500"
          disabled={permissionStatus === 'denied'}
        >
          <Mic className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Phase 2: Recording - Show stop button and timer
  if (isRecording) {
    return (
      <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={stopRecording}
          className="text-red-500 hover:text-red-400 animate-pulse"
        >
          <Square className="h-4 w-4 fill-current" />
        </Button>
        <span className="text-sm text-red-400 font-mono">
          {formatTime(recordingTime)}
        </span>
        <span className="text-xs text-slate-400">Recording...</span>
      </div>
    );
  }

  // Phase 3: Review - Show audio player, send, and cancel buttons
  if (audioBlob) {
    const audioUrl = URL.createObjectURL(audioBlob);
    
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 p-2 bg-slate-700/50 border border-slate-600 rounded-lg">
          <audio
            src={audioUrl}
            controls
            className="flex-1 h-8"
            onLoadedMetadata={() => {
              // Cleanup URL when component unmounts or audio changes
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-slate-400 hover:text-slate-300"
            disabled={isSending}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSend}
            disabled={isSending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-1" />
                Send
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

